import React from 'react';
import {NodeProps, useReactFlow} from 'reactflow';

import {PlaceholderNodeData} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {ReactFlowLockedContext} from 'ui-library/reactflow/LockedContext';
import {BenchlingNode} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNode';

import {FlowchartFeaturesContext, isFlowchartFeatureEnabled} from '../FlowchartFeaturesContext';
import {ProcessFlowchartEditorModeFeatureType} from '../ProcessFlowchartEditorModeConstants';
import createHeaderPropsForNode from './createHeaderPropsForNode';
import ProcessFlowchartNodeToolbar from './ProcessFlowchartNodeToolbar';

/**
 * A placeholder node for when we are in the process of creating a new node in the flowchart.
 * This will be replaced with the actual node when the user finishes editing in the sidepanel for this placeholder node.
 */
export default function PlaceholderNodeRenderer(props: NodeProps<PlaceholderNodeData>): JSX.Element {
  const {data, xPos, yPos} = props;
  const reactFlowInstance = useReactFlow();
  const flowchartFeatures = FlowchartFeaturesContext.useContext();
  const isDeletable = isFlowchartFeatureEnabled(
    flowchartFeatures,
    ProcessFlowchartEditorModeFeatureType.ALLOW_NODE_ADD_DELETE
  );
  const locked = ReactFlowLockedContext.useContext() ?? false;
  const {isDisabled, status, onNodeHeaderClick, onNodeDelete} = data;

  const finalHeaderProps = createHeaderPropsForNode(
    {
      ...data,
      position: {x: xPos, y: yPos},
    },
    reactFlowInstance,
    {
      // Do not allow connecting to this node until it is a real node; we can't handle that today.
      hasRightHandle: false,
    }
  );

  return (
    <div>
      <ProcessFlowchartNodeToolbar
        isLocked={locked || isDisabled}
        onNodeDelete={isDeletable ? onNodeDelete : undefined}
      />
      <div onClick={onNodeHeaderClick} style={{cursor: onNodeHeaderClick != null ? 'pointer' : 'default'}}>
        <BenchlingNode.Standard.Node
          isSelected={props.selected}
          isDisabled={isDisabled}
          status={status}
          headerProps={finalHeaderProps}
          dataTestAttrs={{element: 'PlaceholderNodeRenderer', key: finalHeaderProps.title}}
        />
      </div>
    </div>
  );
}
